import React, { useCallback, useMemo, useState, useEffect } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { all_routes } from "../../router/all_routes"
import { OverlayTrigger, Tooltip } from "react-bootstrap"
import { toast } from "react-toastify"
import JoditEditor from "jodit-react"
import {
  createMailTemplate,
  getEmailPlaceholders,
  getEmailTypes,
  getMailTemplateById,
  sendTestTemplateEmail,
  updateMailTemplate,
  type MailTemplateFormData,
} from "../../../service/mailTemplate"
import { getAllClasses } from "../../../service/classes"
import MultiSelectSection from "../../../core/common/multiSelect"
import { useSelector } from "react-redux"
import { selectSystems } from "../../../core/data/redux/slice/systemsSlice"

interface ClassOption {
  label: string
  value: string
}

type TestVariableRow = { id: string; key: string; value: string; locked: boolean }

const Emailtemplates = () => {
  const routes = all_routes
  const navigate = useNavigate()
  const systems = useSelector(selectSystems)
  const { id } = useParams()
  const [classOptions, setClassOptions] = useState<ClassOption[]>([])
  const [loading, setLoading] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [emailTypes, setEmailTypes] = useState<{ id: string; name: string }[]>(
    []
  )
  const [availableVariables, setAvailableVariables] = useState<
    { label: string; value: string }[]
  >([])
  const [fileInputKey, setFileInputKey] = useState(Date.now())
  const [formData, setFormData] = useState<
    MailTemplateFormData & { classes: string[] }
  >({
    name: "",
    subject: "",
    htmlContent: "",
    category: "",
    variables: [],
    classes: [],
    isActive: true,
    file: null,
  })

  const [formErrors, setFormErrors] = useState<Record<string, string>>({})

  const [showTestEmailModal, setShowTestEmailModal] = useState(false)
  const [sendingTestEmail, setSendingTestEmail] = useState(false)
  const [testToEmail, setTestToEmail] = useState("")
  const [testCcEmail, setTestCcEmail] = useState("")
  const [testBccEmail, setTestBccEmail] = useState("")
  const [testVariables, setTestVariables] = useState<TestVariableRow[]>([])

  const extractPlaceholderKeys = (text: string) => {
    const out = new Set<string>()
    const re = /\{\{\s*([^{}]+?)\s*\}\}/g
    const input = text || ""
    let m: RegExpExecArray | null
    while ((m = re.exec(input))) {
      const key = String(m[1] || "").trim()
      if (key) out.add(key)
    }
    return Array.from(out)
  }

  const editorConfig = useMemo(
    () => ({
      readonly: false,
      height: 300,
      placeholder: "Enter email body content...",
      buttons: [
        "bold",
        "italic",
        "underline",
        "|",
        "ul",
        "ol",
        "|",
        "font",
        "fontsize",
        "|",
        "align",
        "|",
        "undo",
        "redo",
      ],
    }),
    []
  )

  useEffect(() => {
    fetchInitialData()

    if (id) {
      setIsEditMode(true)
      fetchTemplateData(id)
    }
  }, [id])

  const openTestEmailModal = () => {
    if (!id) {
      toast.error("Please save the template first before sending a test email")
      return
    }

    // Always open with a fresh/empty form (no prefilled values)
    setTestToEmail("")
    setTestCcEmail("")
    setTestBccEmail("")

    const keysFromTemplate = formData.variables || []
    const keysFromContent = [
      ...extractPlaceholderKeys(formData.subject || ""),
      ...extractPlaceholderKeys(formData.htmlContent || ""),
    ]
    const keys = Array.from(
      new Set([...keysFromTemplate, ...keysFromContent])
    ).filter(Boolean)
    setTestVariables(
      keys.map(key => ({
        id: `locked-${key}`,
        key,
        value: "",
        locked: true,
      }))
    )

    setShowTestEmailModal(true)
  }

  const closeTestEmailModal = () => setShowTestEmailModal(false)

  const updateTestVariable = (
    index: number,
    patch: Partial<TestVariableRow>
  ) => {
    setTestVariables(prev =>
      prev.map((r, i) => (i === index ? { ...r, ...patch } : r))
    )
  }

  const addCustomTestVariable = () => {
    const id = `custom-${Date.now().toString(36)}-${Math.random()
      .toString(36)
      .slice(2)}`
    setTestVariables(prev => [
      ...prev,
      { id, key: "", value: "", locked: false },
    ])
  }

  const removeCustomTestVariable = (index: number) => {
    setTestVariables(prev => prev.filter((_, i) => i !== index))
  }

  const handleSendTestEmail = async () => {
    if (!id) return

    if (!testToEmail.trim()) {
      toast.error("Recipient email is required")
      return
    }

    const variables: Record<string, string> = {}
    testVariables.forEach(row => {
      const k = row.key.trim()
      if (!k) return
      variables[k] = row.value ?? ""
    })

    try {
      setSendingTestEmail(true)
      const response = await sendTestTemplateEmail(id, {
        to: testToEmail.trim(),
        cc: testCcEmail.trim() || undefined,
        bcc: testBccEmail.trim() || undefined,
        variables,
      })

      if (response.success) {
        toast.success(response.message || "Test email sent successfully")
        setShowTestEmailModal(false)
      } else {
        toast.error(response.message || "Failed to send test email")
      }
    } catch (error: any) {
      const missingVariables =
        error?.response?.data?.data?.missingVariables ||
        error?.response?.data?.missingVariables
      if (Array.isArray(missingVariables) && missingVariables.length > 0) {
        toast.error(
          `Missing variables: ${missingVariables.join(", ")}`
        )
      } else {
        toast.error(error?.response?.data?.message || "Failed to send test email")
      }
    } finally {
      setSendingTestEmail(false)
    }
  }

  const fetchInitialData = async () => {
    try {
      setLoading(true)

      // 1. Email Types
      const typesResponse = await getEmailTypes()
      if (typesResponse.success) {
        const transformedTypes = typesResponse.data.map(type => ({
          id: type.id,
          name: type.label,
        }))
        setEmailTypes(transformedTypes)
      }

      // 2. Placeholders / Variables
      const placeholdersResponse = await getEmailPlaceholders()
      if (placeholdersResponse.success) {
        const transformedPlaceholders = placeholdersResponse.data.map(
          (item: any) => ({
            label: item.description,
            value: item.id,
          })
        )
        setAvailableVariables(transformedPlaceholders)
      }

      /* @TODO: Will introduce system dropdown */
      const classesResponse = await getAllClasses({
        limit: 100,
        status: "active",
        system: systems[0]._id,
      })
      console.log("RAW CLASSES RESPONSE:", classesResponse)

      const classesArray = classesResponse.classes || []

      if (Array.isArray(classesArray) && classesArray.length > 0) {
        const formatted = classesArray.map((cls: any) => {
          const label = cls.name || cls.classIdentifier || "Unnamed Class"
          const value = cls._id
          return { label, value }
        })
        setClassOptions(formatted)
      } else {
        setClassOptions([])
      }
    } catch (error: any) {
      console.error("Error fetching initial data:", error)
      toast.error(
        error.response?.data?.message || "Failed to load initial data"
      )
    } finally {
      setLoading(false)
    }
  }

  const fetchTemplateData = async (templateId: string) => {
    try {
      setLoading(true)
      const response = await getMailTemplateById(templateId)

      if (response.success) {
        setFormData({
          name: response.data.name,
          subject: response.data.subject,
          htmlContent: response.data.htmlContent,
          category: response.data.category,
          variables: response.data.variables || [],
          classes:
            response.data.classes?.map((c: any) =>
              typeof c === "object" ? c._id : c
            ) || [],
          isActive: response.data.isActive,
          file: null,
        })
      }
    } catch (error: any) {
      console.error("Error loading template:", error)
      toast.error(error.response?.data?.message || "Failed to load template")
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target
    const checked = (e.target as HTMLInputElement).checked
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }))
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: "",
      }))
    }
  }

  const handleEditorChange = useCallback((content: string) => {
    setFormData(prev => ({
      ...prev,
      htmlContent: content,
    }))
    setFormErrors(prev =>
      prev.htmlContent ? { ...prev, htmlContent: "" } : prev
    )
  }, [])

  const noopEditorChange = useCallback(() => {}, [])
  const handleClassesChange = (selected: ClassOption[]) => {
    const selectedIds = selected.map(opt => opt.value)
    setFormData(prev => ({ ...prev, classes: selectedIds }))

    if (formErrors.classes) {
      setFormErrors(prev => ({ ...prev, classes: "" }))
    }
  }
  const handleVariableSelect = (variable: { label: string; value: string }) => {
    if (!formData.variables.includes(variable.value)) {
      setFormData({
        ...formData,
        variables: [...formData.variables, variable.value],
      })
    }
    const variableText = `{{${variable.value}}}`

    setFormData(prev => ({
      ...prev,
      htmlContent: prev.htmlContent + variableText,
    }))
  }

  const removeVariable = (index: number) => {
    const newVariables = [...formData.variables]
    const removedVariable = newVariables.splice(index, 1)

    const variableText = `{{${removedVariable[0]}}}`
    const regex = new RegExp(
      variableText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
      "g"
    )
    const updatedContent = formData.htmlContent.replace(regex, "")

    setFormData({
      ...formData,
      variables: newVariables,
      htmlContent: updatedContent.trim(),
    })
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file && file.size > 10 * 1024 * 1024) {
      toast.error("File size should not exceed 10MB")
      return
    }
    setFormData({
      ...formData,
      file: file || null,
    })
  }

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}

    if (!formData.name.trim()) {
      errors.name = "Template name is required"
    }
    if (!formData.subject.trim()) {
      errors.subject = "Email subject is required"
    }
    if (
      !formData.htmlContent.trim() ||
      formData.htmlContent === "<p><br></p>"
    ) {
      errors.htmlContent = "Email body is required"
    }
    if (!formData.category) {
      errors.category = "Email type is required"
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSaveTemplate = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      toast.error("Please fill all required fields correctly")
      return
    }

    try {
      setLoading(true)

      if (isEditMode && id) {
        const response = await updateMailTemplate(id, formData)
        if (response.success) {
          toast.success("Email template updated successfully!")
          navigate(routes.emailTemplatesList)
        }
      } else {
        const response = await createMailTemplate(formData)
        if (response.success) {
          toast.success("Email template created successfully!")
          resetForm()
          navigate(routes.emailTemplatesList)
        }
      }
    } catch (error: any) {
      console.error("Error saving template:", error)
      toast.error(error.response?.data?.message || "Failed to save template")
    } finally {
      setLoading(false)
    }
  }

  const resetForm = () => {
    setFormData({
      name: "",
      subject: "",
      htmlContent: "",
      category: "",
      variables: [],
      classes: [],
      isActive: true,
      file: null,
    })
    setFormErrors({})
    setFileInputKey(Date.now())
  }

  const getEmailTypeName = (typeId: string) => {
    const type = emailTypes.find(t => t.id === typeId)
    return type ? type.name : ""
  }

  return (
    <div>
      <div className="page-wrapper">
        <div className="content bg-white">
          <div className="d-md-flex d-block align-items-center justify-content-between border-bottom pb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">
                {isEditMode ? "Edit Email Template" : "Create Email Template"}
              </h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to="index">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="#">Settings</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to={routes.emailTemplatesList}>Email Templates</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {isEditMode ? "Edit Template" : "Create Template"}
                  </li>
                </ol>
              </nav>
            </div>
            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
              <div className="pe-1 mb-2">
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="tooltip-top">Refresh</Tooltip>}
                >
                  <button
                    className="btn btn-outline-light bg-white btn-icon me-1"
                    onClick={resetForm}
                    disabled={loading}
                  >
                    <i className="ti ti-refresh" />
                  </button>
                </OverlayTrigger>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-xxl-16 col-xl-15">
              <div className="flex-fill border-start ps-3">
                <div className="d-flex align-items-center justify-content-between flex-wrap border-bottom pt-3 mb-3">
                  <div className="mb-3">
                    <h5 className="mb-1">
                      {isEditMode
                        ? "Edit Email Template"
                        : "Create New Email Template"}
                    </h5>
                    <p>
                      {isEditMode
                        ? "Update the template details below"
                        : "Fill the form below to create a new email template"}
                    </p>
                  </div>
                </div>

                {loading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <div className="card ms-2">
                    <div className="card-body">
                      <form onSubmit={handleSaveTemplate}>
                        <div className="row">
                          <div className="col-md-6">
                            <div className="mb-3">
                              <label className="form-label">
                                Email Type{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <select
                                name="category"
                                className={`form-control ${
                                  formErrors.category ? "is-invalid" : ""
                                }`}
                                value={formData.category}
                                onChange={handleInputChange}
                                required
                              >
                                <option value="">Select Email Type</option>
                                {emailTypes.map(type => (
                                  <option key={type.id} value={type.id}>
                                    {type.name}
                                  </option>
                                ))}
                              </select>
                              {formErrors.category && (
                                <div className="invalid-feedback">
                                  {formErrors.category}
                                </div>
                              )}
                              {isEditMode && formData.category && (
                                <small className="text-muted">
                                  Current selection:{" "}
                                  {getEmailTypeName(formData.category)}
                                </small>
                              )}
                            </div>
                          </div>
                          <div className="col-md-6">
                            {/* Template Name */}
                            <div className="mb-3">
                              <label className="form-label">
                                Template Name{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                name="name"
                                className={`form-control ${
                                  formErrors.name ? "is-invalid" : ""
                                }`}
                                placeholder="Enter Template Name"
                                value={formData.name}
                                onChange={handleInputChange}
                                required
                              />
                              {formErrors.name && (
                                <div className="invalid-feedback">
                                  {formErrors.name}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="col-md-6 mb-3">
                            <label className="form-label">
                              Classes <span className="text-danger">*</span>
                            </label>
                            <MultiSelectSection
                              options={classOptions}
                              value={classOptions.filter(opt =>
                                formData.classes.includes(opt.value)
                              )}
                              onChange={handleClassesChange}
                              placeholder="Select one or multiple classes..."
                            />
                            {formErrors.classes && (
                              <div className="text-danger mt-1 fs-12">
                                {formErrors.classes}
                              </div>
                            )}
                          </div>

                          <div className="col-md-12">
                            {/* Email Subject */}
                            <div className="mb-3">
                              <label className="form-label">
                                Email Subject{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                name="subject"
                                className={`form-control ${
                                  formErrors.subject ? "is-invalid" : ""
                                }`}
                                placeholder="Enter Email Subject"
                                value={formData.subject}
                                onChange={handleInputChange}
                                required
                              />
                              {formErrors.subject && (
                                <div className="invalid-feedback">
                                  {formErrors.subject}
                                </div>
                              )}
                            </div>

                            {/* Variables/Tags Section */}
                            <div className="mb-3">
                              <label className="form-label">
                                Available Variables/Tags
                              </label>
                              <div className="d-flex flex-wrap gap-2 mb-2">
                                {availableVariables.map((variable, index) => (
                                  <OverlayTrigger
                                    key={index}
                                    placement="top"
                                    overlay={
                                      <Tooltip>
                                        Click to insert: {variable.value}
                                      </Tooltip>
                                    }
                                  >
                                    <button
                                      type="button"
                                      className="btn btn-outline-primary btn-sm"
                                      onClick={() =>
                                        handleVariableSelect(variable)
                                      }
                                    >
                                      {variable.label}
                                    </button>
                                  </OverlayTrigger>
                                ))}
                              </div>

                              {/* Selected Variables Display */}
                              {formData.variables.length > 0 && (
                                <div className="p-2 border rounded mb-3">
                                  <label className="form-label mb-2">
                                    Selected Variables:
                                  </label>
                                  <div className="d-flex flex-wrap gap-2">
                                    {formData.variables.map(
                                      (variableValue, index) => (
                                        <div
                                          key={index}
                                          className="badge bg-primary d-flex align-items-center"
                                        >
                                          {`{{${variableValue}}}`}
                                          <button
                                            type="button"
                                            className="btn-close btn-close-white ms-2"
                                            style={{ fontSize: "10px" }}
                                            onClick={() =>
                                              removeVariable(index)
                                            }
                                          ></button>
                                        </div>
                                      )
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Variables Help Text */}
                              <div className="mt-2 p-2 border rounded bg-light">
                                <small className="text-muted">
                                  <strong>How to use variables:</strong>
                                  <ul className="mb-0 mt-1">
                                    <li>
                                      Click on any variable button to insert it
                                      into the email body
                                    </li>
                                    <li>
                                      Variables will appear as{" "}
                                      <code>{`{{variable_name}}`}</code>
                                    </li>
                                    <li>
                                      These will be replaced with actual values
                                      when sending emails
                                    </li>
                                  </ul>
                                </small>
                              </div>
                            </div>

                            {/* Email Body - Jodit Rich Text Editor */}
                            <div className="mb-3">
                              <label className="form-label">
                                Email Body{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <div
                                className={`${
                                  formErrors.htmlContent ? "border-danger" : ""
                                }`}
                              >
                                <JoditEditor
                                  value={formData.htmlContent}
                                  config={editorConfig}
                                  onBlur={handleEditorChange}
                                  // Avoid re-render on every keystroke (caret/focus issues)
                                  onChange={noopEditorChange}
                                />
                              </div>
                              <small className="text-muted">
                                Use the variables above by clicking on them. Use
                                the toolbar above for rich text formatting.
                              </small>
                              {formErrors.htmlContent && (
                                <div className="invalid-feedback d-block">
                                  {formErrors.htmlContent}
                                </div>
                              )}
                            </div>

                            {/* Attachment */}
                            <div className="mb-3">
                              <label className="form-label">
                                Attachment (Optional)
                              </label>
                              <input
                                key={fileInputKey}
                                type="file"
                                className="form-control"
                                onChange={handleFileChange}
                              />
                              {formData.file && (
                                <div className="mt-2">
                                  <small className="text-muted">
                                    Selected file: {formData.file.name}
                                  </small>
                                </div>
                              )}
                            </div>

                            {/* Test Email (Edit mode only) */}
                            {isEditMode && (
                              <div className="mb-3">
                                <button
                                  type="button"
                                  className="btn btn-outline-primary"
                                  onClick={openTestEmailModal}
                                  disabled={loading}
                                >
                                  Send Test Email
                                </button>
                              </div>
                            )}

                            {/* Status Toggle */}
                            <div className="mb-3">
                              <div className="d-flex align-items-center justify-content-between">
                                <div className="status-title">
                                  <h5>Status</h5>
                                  <p>Change the Status by toggle</p>
                                </div>
                                <div className="form-check form-switch">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    id="isActive"
                                    name="isActive"
                                    checked={formData.isActive}
                                    onChange={handleInputChange}
                                  />
                                  <label
                                    className="form-check-label"
                                    htmlFor="isActive"
                                  >
                                    {formData.isActive ? "Active" : "Inactive"}
                                  </label>
                                </div>
                              </div>
                            </div>

                            {/* Submit Button */}
                            <div className="d-flex justify-content-end">
                              <button
                                type="button"
                                className="btn btn-light me-2"
                                onClick={() =>
                                  navigate(routes.emailTemplatesList)
                                }
                                disabled={loading}
                              >
                                Cancel
                              </button>
                              <button
                                type="submit"
                                className="btn btn-primary"
                                disabled={loading}
                              >
                                {loading ? (
                                  <>
                                    <span
                                      className="spinner-border spinner-border-sm me-2"
                                      role="status"
                                      aria-hidden="true"
                                    ></span>
                                    {isEditMode ? "Updating..." : "Saving..."}
                                  </>
                                ) : isEditMode ? (
                                  "Update Template"
                                ) : (
                                  "Save Template"
                                )}
                              </button>
                            </div>
                          </div>
                        </div>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Test Email Modal */}
      <div
        className={`modal fade ${showTestEmailModal ? "show d-block" : ""}`}
        style={{
          backgroundColor: showTestEmailModal
            ? "rgba(0,0,0,0.5)"
            : "transparent",
        }}
        role="dialog"
        aria-modal={showTestEmailModal ? "true" : "false"}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Send Test Email</h5>
              <button
                type="button"
                className="btn-close custom-btn-close"
                onClick={closeTestEmailModal}
                aria-label="Close"
                disabled={sendingTestEmail}
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <div className="modal-body">
              <div className="row">
                <div className="col-md-12 mb-3">
                  <label className="form-label">
                    To <span className="text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    placeholder="test@example.com"
                    value={testToEmail}
                    onChange={e => setTestToEmail(e.target.value)}
                    disabled={sendingTestEmail}
                  />
                </div>

                <div className="col-md-6 mb-3">
                  <label className="form-label">CC (optional)</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="cc@example.com"
                    value={testCcEmail}
                    onChange={e => setTestCcEmail(e.target.value)}
                    disabled={sendingTestEmail}
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">BCC (optional)</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="bcc@example.com"
                    value={testBccEmail}
                    onChange={e => setTestBccEmail(e.target.value)}
                    disabled={sendingTestEmail}
                  />
                </div>

                <div className="col-md-12">
                  <div className="d-flex align-items-center justify-content-between">
                    <label className="form-label mb-0">Variables</label>
                  </div>

                  <div className="mt-2 d-flex flex-column gap-2">
                    {testVariables.length === 0 ? (
                      <small className="text-muted">
                        No variables found in this template.
                      </small>
                    ) : (
                      testVariables.map((row, index) => (
                        <div
                          key={row.id}
                          className="d-flex gap-2 align-items-center"
                        >
                          <div
                            className="form-control bg-light"
                            style={{ width: "40%", cursor: "not-allowed" }}
                            title={`{{${row.key}}}`}
                          >
                            <code style={{ color: "#000" }}>
                              {`{{${row.key}}}`}
                            </code>
                          </div>
                          <input
                            type="text"
                            className="form-control"
                            placeholder={`Enter value for ${row.key}...`}
                            value={row.value}
                            disabled={sendingTestEmail}
                            onChange={e =>
                              updateTestVariable(index, {
                                value: e.target.value,
                              })
                            }
                          />
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-light"
                onClick={closeTestEmailModal}
                disabled={sendingTestEmail}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSendTestEmail}
                disabled={sendingTestEmail}
              >
                {sendingTestEmail ? "Sending..." : "Send"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Emailtemplates
